import { useMemo } from 'react';
import { Match, Player, PlayerStats } from '../types';

interface MatchScore {
  team1Goals: number;
  team2Goals: number;
  team1Name: string;
  team2Name: string;
}

interface Teams {
  team1: (Player | null)[];
  team2: (Player | null)[];
}

interface UseMatchCalculationsProps {
  match: Match | null;
  teams: Teams | null;
  matchReport: any;
  selfReports: { [playerId: string]: any };
}

interface UseMatchCalculationsReturn {
  playerStatsMap: { [playerId: string]: PlayerStats };
  calculatedMatchScore: MatchScore | null;
  mvpPlayerIds: string[];
  topScorerPlayerIds: string[];
  topAssisterPlayerIds: string[];
  topSaverPlayerIds: string[];
  winningTeamIndex: number | null;
  isDraw: boolean;
  getCombinedPlayerStats: (playerId: string) => any;
  combinedPlayerStatsMap: { [playerId: string]: any };
  hasGuestPlayers: boolean;
  guestPlayers: Player[];
}

export const useMatchCalculations = ({
  match,
  teams,
  matchReport,
  selfReports
}: UseMatchCalculationsProps): UseMatchCalculationsReturn => {
  // Preparar stats por jugador para pasar a los componentes
  // Combina stats del reporte tradicional con stats validadas de auto-reportes
  const playerStatsMap = useMemo(() => {
    const statsMap: { [playerId: string]: PlayerStats } = {};
    
    // Agregar stats del reporte tradicional (si existe)
    if (matchReport?.stats) {
      matchReport.stats.forEach((stat: any) => {
        statsMap[stat.playerId] = {
          ...stat,
          // Incluir los logros calculados por el backend
          isMVP: stat.isMVP || false,
          isTopScorer: stat.isTopScorer || false,
          isTopAssister: stat.isTopAssister || false,
          isTopSaver: stat.isTopSaver || false
        };
      });
    }
    
    // Agregar stats validadas de auto-reportes
    Object.entries(selfReports).forEach(([playerId, selfReport]) => {
      if (selfReport && selfReport.isSubmitted && selfReport.validationStatus === 'APPROVED') {
        // Buscar el jugador para obtener su nombre
        const player = match?.players.find(p => p.id === playerId);
        if (player) {
          // Si ya existe en statsMap, actualizar solo las stats, mantener los logros del backend
          const existingStats = statsMap[playerId];
          statsMap[playerId] = {
            playerId: playerId,
            name: player.name,
            goals: selfReport.goals || 0,
            assists: selfReport.assists || 0,
            saves: selfReport.saves || 0,
            votes: existingStats?.votes || 0,
            votePercentage: existingStats?.votePercentage || 0,
            // Mantener los logros del backend si existen, sino usar false
            isMVP: existingStats?.isMVP || false,
            isTopScorer: existingStats?.isTopScorer || false,
            isTopAssister: existingStats?.isTopAssister || false,
            isTopSaver: existingStats?.isTopSaver || false
          };
        }
      }
    });

    // Agregar stats pendientes de validación de auto-reportes (para mostrar en la UI)
    Object.entries(selfReports).forEach(([playerId, selfReport]) => {
      if (selfReport && selfReport.isSubmitted && selfReport.validationStatus === 'PENDING') {
        // Buscar el jugador para obtener su nombre
        const player = match?.players.find(p => p.id === playerId);
        if (player) {
          // Solo agregar si no existe ya en statsMap (para no sobrescribir stats validadas)
          if (!statsMap[playerId]) {
            // Buscar si ya existen stats en matchReport para mantener los logros
            const existingStatsFromReport = matchReport?.stats?.find((s: any) => s.playerId === playerId);
            
            statsMap[playerId] = {
              playerId: playerId,
              name: player.name,
              goals: selfReport.goals || 0,
              assists: selfReport.assists || 0,
              saves: selfReport.saves || 0,
              votes: existingStatsFromReport?.votes || 0,
              votePercentage: existingStatsFromReport?.votePercentage || 0,
              // Mantener los logros existentes del matchReport si los hay
              isMVP: existingStatsFromReport?.isMVP || false,
              isTopScorer: existingStatsFromReport?.isTopScorer || false,
              isTopAssister: existingStatsFromReport?.isTopAssister || false,
              isTopSaver: existingStatsFromReport?.isTopSaver || false
            };
          }
        }
      }
    });
    
    return statsMap;
  }, [matchReport, selfReports, match]);

  // Calcular score del partido usando estadísticas combinadas
  const calculatedMatchScore = useMemo((): MatchScore | null => {
    // Si el admin subió un resultado manual, usarlo (aunque uno sea null)
    if (
      match &&
      (typeof match.team1Goals === 'number' || typeof match.team2Goals === 'number')
    ) {
      return {
        team1Goals: typeof match.team1Goals === 'number' ? match.team1Goals : 0,
        team2Goals: typeof match.team2Goals === 'number' ? match.team2Goals : 0,
        team1Name: match.team1Name,
        team2Name: match.team2Name,
      };
    }

    // Si no, calcularlo sumando los goles de los jugadores
    if (!match || !teams || Object.keys(playerStatsMap).length === 0) return null;

    const team1PlayerIds = teams.team1?.map((p) => p?.id).filter(Boolean) || [];
    const team2PlayerIds = teams.team2?.map((p) => p?.id).filter(Boolean) || [];

    let team1Goals = 0;
    let team2Goals = 0;

    Object.values(playerStatsMap).forEach((stat) => {
      if (team1PlayerIds.includes(stat.playerId)) {
        team1Goals += stat.goals || 0;
      } else if (team2PlayerIds.includes(stat.playerId)) {
        team2Goals += stat.goals || 0;
      }
    });

    return {
      team1Goals,
      team2Goals,
      team1Name: match.team1Name,
      team2Name: match.team2Name,
    };
  }, [match, teams, playerStatsMap]);

  // Detectar MVPs (mayor cantidad de votos)
  const mvpPlayerIds = useMemo(() => {
    // Usar los logros calculados por el backend
    const statsArray = Object.values(playerStatsMap);
    return statsArray.filter(s => s.isMVP).map(s => s.playerId);
  }, [playerStatsMap]);

  // Detectar goleadores (mayor cantidad de goles)
  const topScorerPlayerIds = useMemo(() => {
    // Usar los logros calculados por el backend
    const statsArray = Object.values(playerStatsMap);
    return statsArray.filter(s => s.isTopScorer).map(s => s.playerId);
  }, [playerStatsMap]);

  // Detectar asistentes (mayor cantidad de asistencias)
  const topAssisterPlayerIds = useMemo(() => {
    // Usar los logros calculados por el backend
    const statsArray = Object.values(playerStatsMap);
    return statsArray.filter(s => s.isTopAssister).map(s => s.playerId);
  }, [playerStatsMap]);

  // Detectar jugadores con más atajadas (Guante de Oro)
  const topSaverPlayerIds = useMemo(() => {
    // Usar los logros calculados por el backend
    const statsArray = Object.values(playerStatsMap);
    return statsArray.filter(s => s.isTopSaver).map(s => s.playerId);
  }, [playerStatsMap]);

  // Determinar equipo ganador basado en el score final
  const winningTeamIndex = useMemo(() => {
    if (!calculatedMatchScore) return null;
    
    const { team1Goals, team2Goals } = calculatedMatchScore;
    
    if (team1Goals > team2Goals) {
      return 0; // Team 1 ganó
    } else if (team2Goals > team1Goals) {
      return 1; // Team 2 ganó
    }
    
    return null; // Empate
  }, [calculatedMatchScore]);

  // Determinar si hay empate
  const isDraw = useMemo(() => {
    if (!calculatedMatchScore) return false;
    
    const { team1Goals, team2Goals } = calculatedMatchScore;
    return team1Goals === team2Goals;
  }, [calculatedMatchScore]);

  // Función para obtener las stats combinadas (actuales + reportadas pendientes)
  const getCombinedPlayerStats = useMemo(() => {
    return (playerId: string) => {
      if (!match || match.status !== 'FINISHED') {
        // Retornar stats vacías en lugar de null
        return {
          playerId,
          goals: 0,
          assists: 0,
          saves: 0,
          votes: 0,
          votePercentage: 0,
          isMVP: false,
          isTopScorer: false,
          isTopAssister: false,
          isTopSaver: false
        };
      }
      
      const currentStats = playerStatsMap[playerId];
      
      // Buscar el jugador por playerId
      const player = match.players.find(p => p.id === playerId);
      if (!player || !player.userId) {
        // Si no es usuario registrado, retornar stats vacías
        return {
          playerId,
          goals: 0,
          assists: 0,
          saves: 0,
          votes: 0,
          votePercentage: 0,
          isMVP: false,
          isTopScorer: false,
          isTopAssister: false,
          isTopSaver: false
        };
      }
      
      // Verificar si este jugador tiene stats pendientes
      const selfReport = selfReports[player.id];
      const hasPending = selfReport && selfReport.isSubmitted && selfReport.validationStatus === 'PENDING';
      
      if (!hasPending) {
        // Si no hay stats pendientes, devolver las actuales o stats vacías
        return currentStats || {
          playerId,
          goals: 0,
          assists: 0,
          saves: 0,
          votes: 0,
          votePercentage: 0,
          isMVP: false,
          isTopScorer: false,
          isTopAssister: false,
          isTopSaver: false
        };
      }
      
      // Si hay stats pendientes, usar el selfReport ya obtenido
      if (!selfReport) {
        // Si no hay self-report, devolver stats actuales o vacías
        return currentStats || {
          playerId,
          goals: 0,
          assists: 0,
          saves: 0,
          votes: 0,
          votePercentage: 0,
          isMVP: false,
          isTopScorer: false,
          isTopAssister: false,
          isTopSaver: false
        };
      }
      
      // Combinar stats actuales con las reportadas pendientes
      return {
        ...currentStats,
        playerId,
        goals: selfReport.goals || 0,
        assists: selfReport.assists || 0,
        saves: selfReport.saves || 0,
        // Mantener otros campos como votes, votePercentage, etc.
        votes: currentStats?.votes || 0,
        votePercentage: currentStats?.votePercentage || 0,
        // Mantener logros
        isMVP: currentStats?.isMVP || false,
        isTopScorer: currentStats?.isTopScorer || false,
        isTopAssister: currentStats?.isTopAssister || false,
        isTopSaver: currentStats?.isTopSaver || false,
        // Agregar indicador de que las stats están pendientes
        isPendingValidation: true
      };
    };
  }, [match, playerStatsMap, selfReports]);

  // Crear mapa de stats combinadas para todos los jugadores
  const combinedPlayerStatsMap = useMemo(() => {
    if (!match) return {};
    
    const allPlayers = [...match.players, ...(match.substitutes || [])];
    const statsMap: { [playerId: string]: any } = {};
    
    allPlayers.forEach(player => {
      statsMap[player.id] = getCombinedPlayerStats(player.id);
    });
    
    return statsMap;
  }, [match, getCombinedPlayerStats]);

  // Check if there are guest players (players without userId)
  const hasGuestPlayers = useMemo(() => {
    if (!match) return false;
    const allPlayers = [...match.players, ...(match.substitutes || [])];
    return allPlayers.some(player => !player.userId);
  }, [match]);

  // Get guest players for goals reporting
  const guestPlayers = useMemo(() => {
    if (!match) return [];
    const allPlayers = [...match.players, ...(match.substitutes || [])];
    return allPlayers.filter(player => !player.userId);
  }, [match]);

  return {
    playerStatsMap,
    calculatedMatchScore,
    mvpPlayerIds,
    topScorerPlayerIds,
    topAssisterPlayerIds,
    topSaverPlayerIds,
    winningTeamIndex,
    isDraw,
    getCombinedPlayerStats,
    combinedPlayerStatsMap,
    hasGuestPlayers,
    guestPlayers
  };
};
